/**
 * 评测配置 › 判官提示（`#eval-judges`，后台页面对齐批 4；只读）：每个判官判什么、结论进不进通过率、所用模型、
 * 提示词全文（默认折叠）、给什么 ／ 不给什么、输出结构。提示词和模型随题库快照（接口 `/bank/snapshots/{id}/judges`），
 * 指南、编造两个判官复用 NL 合规判官的同一份提示词；CX 只列一行，口径与全文在「体验评分」页。
 * 「结论怎么用」的编号点了去「判分规则」页打开那条。改提示词走 Loop 侧再导出新题库快照。
 */
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { CodeBlock } from "@/components/ui/code-block";
import { EmptyState } from "@/components/ui/feedback";
import { type PlanJudge, usePlanBankJudges, usePlanBanks } from "@/lib/api/plan-eval-config";

import { PageJump } from "../kit/page-jump";
import { type PlanColumn, PlanTable } from "../kit/plan-table";
import { BankPicker, BlockState, Field } from "./shared";

const VERDICT_TONE: Record<string, BadgeTone> = {
	一票否决: "danger",
	观测列: "neutral",
	诊断: "info",
};

const LINK_CLS = "text-text-secondary underline-offset-2 hover:text-brand-accent hover:underline";

/** 判分规则编号：点了去「判分规则」页并打开那条 */
function RuleLink({ id }: { id: string }) {
	return (
		<Link
			to="/plan-lab"
			search={{ rule: id }}
			hash="eval-rules"
			title={`去判分规则看 ${id}`}
			onClick={(e) => e.stopPropagation()}
			className={`font-mono text-xs ${LINK_CLS}`}
			data-testid="plan-judge-rule-link"
		>
			{id}
		</Link>
	);
}

function promptText(j: PlanJudge, byKey: Map<string, PlanJudge>): string {
	if (j.link) return "见体验评分页";
	if (j.reuses) return `同 ${byKey.get(j.reuses)?.name ?? j.reuses}`;
	return j.prompt_key;
}

function Bullets({ items }: { items: string[] }) {
	return (
		<ul className="list-disc space-y-0.5 pl-4 text-sm text-text-primary">
			{items.map((x) => (
				<li key={x}>{x}</li>
			))}
		</ul>
	);
}

function PromptParts({ j }: { j: PlanJudge }) {
	return (
		<div className="space-y-2" data-testid="plan-judge-prompts">
			{j.prompt_keys.map((k) => {
				const part = k.split(".").pop() ?? "";
				const vars = j.variables[part] ?? [];
				const text = j.prompts[k];
				return (
					<details
						key={k}
						className="rounded-lg border border-surface-border px-3 py-2"
						data-testid="plan-judge-prompt"
					>
						<summary className="cursor-pointer text-sm text-text-primary">
							<span className="font-mono text-xs">{k}</span>
							<span className="ml-2 text-xs text-text-muted">
								{text ? `${text.length} 字` : "这份快照里没有"}
								{vars.length ? ` · 注入 ${vars.map((v) => `{{${v.name}}}`).join(" ")}` : ""}
							</span>
						</summary>
						<div className="mt-2 space-y-2">
							{vars.length ? (
								<ul className="space-y-0.5 text-xs text-text-secondary">
									{vars.map((v) => (
										<li key={v.name}>
											<span className="font-mono text-text-primary">{`{{${v.name}}}`}</span>：
											{v.desc}
										</li>
									))}
								</ul>
							) : null}
							{text ? <CodeBlock code={text} maxHeight={480} /> : null}
						</div>
					</details>
				);
			})}
		</div>
	);
}

function JudgeCard({ j, byKey }: { j: PlanJudge; byKey: Map<string, PlanJudge> }) {
	return (
		<Card data-testid={`plan-judge-${j.key}`}>
			<CardContent className="space-y-3 py-4">
				<div className="flex flex-wrap items-center gap-2">
					<span className="text-sm font-semibold text-text-primary">{j.name}</span>
					<Badge tone={VERDICT_TONE[j.verdict] ?? "muted"}>{j.verdict}</Badge>
					{j.rule_ids.map((id) => (
						<RuleLink key={id} id={id} />
					))}
					{!j.enabled ? <Badge tone="muted">这份快照没有，不评</Badge> : null}
					<span className="ml-auto font-mono text-xs text-text-muted">{j.model || "—"}</span>
				</div>
				<div className="grid gap-x-6 gap-y-3 md:grid-cols-2">
					<Field label="判什么">{j.judges_what}</Field>
					<Field label="结论怎么用">{j.verdict_note}</Field>
					<Field label="什么时候跑">{j.when}</Field>
					<Field label="出错时">{j.failure}</Field>
				</div>
				{j.link ? (
					<div className="text-sm text-text-secondary">
						七条理由、1～5 分口径、封顶与待复核规则和提示词全文见{" "}
						<Link
							to="/plan-lab"
							hash={j.link}
							className={LINK_CLS}
							data-testid="plan-judge-cx-link"
						>
							体验评分
						</Link>
						。
					</div>
				) : (
					<>
						<div className="grid gap-x-6 gap-y-3 md:grid-cols-2">
							<div className="space-y-1">
								<div className="text-xs text-text-muted">给判官什么</div>
								<Bullets items={j.inputs} />
							</div>
							<div className="space-y-1">
								<div className="text-xs text-text-muted">不给什么</div>
								<Bullets items={j.not_given} />
							</div>
						</div>
						{j.fixed_assertions?.length ? (
							<div className="space-y-1" data-testid="plan-judge-fixed-assertions">
								<div className="text-xs text-text-muted">
									固定断言（写在代码里，每次试跑都判这 {j.fixed_assertions.length} 条）
								</div>
								<ol className="list-decimal space-y-1 pl-4 text-sm text-text-primary">
									{j.fixed_assertions.map((a) => (
										<li key={a}>{a}</li>
									))}
								</ol>
							</div>
						) : null}
						<div className="space-y-1">
							<div className="text-xs text-text-muted">输出结构</div>
							<CodeBlock code={j.output} />
						</div>
						{j.reuses ? (
							<div className="text-xs text-text-secondary">
								提示词：和 {byKey.get(j.reuses)?.name ?? j.reuses}
								用同一份（{j.prompt_key}），只是把上面的材料拼进对话、断言换成本判官的。
							</div>
						) : (
							<PromptParts j={j} />
						)}
					</>
				)}
			</CardContent>
		</Card>
	);
}

export function EvalJudgesTab() {
	const list = usePlanBanks();
	const rows = list.data?.rows ?? [];
	const [bankId, setBankId] = useState("");
	useEffect(() => {
		const first = rows[0];
		if (!bankId && first) setBankId(first.bank_id);
	}, [bankId, rows]);
	const id = bankId || rows[0]?.bank_id || null;
	const q = usePlanBankJudges(id);

	if (list.isLoading || list.isError || !list.data)
		return <BlockState testId="plan-block-judges-head" q={list} failTestId="plan-judges-fail" />;
	if (!rows.length || !id)
		return (
			<Card data-testid="plan-block-judges-head">
				<CardContent>
					<EmptyState title="还没有题库快照" desc="判官的提示词和模型从题库快照取，先导出一份。" />
				</CardContent>
			</Card>
		);

	const d = q.data;
	const head = (
		<Card data-testid="plan-block-judges-head">
			<CardContent className="space-y-3 py-4">
				<div className="flex flex-wrap items-center gap-3">
					<BankPicker rows={rows} value={id} onChange={setBankId} />
					<span className="text-xs text-text-muted">
						只读：判官提示词和模型随 <PageJump to="eval-bank" />
						，改动走 Loop 侧再导出
					</span>
				</div>
				{d ? (
					<div
						className="grid gap-x-6 gap-y-3 sm:grid-cols-2 xl:grid-cols-4"
						data-testid="plan-judges-meta"
					>
						<Field label="判官模型（全部判官同一个）">
							<span className="font-mono">{d.model || "—"}</span>
						</Field>
						<Field label="调用方式">温度 {d.temperature} ＋ JSON 模式；解析失败重试一次</Field>
						<Field label="判官指纹">
							<span className="font-mono text-xs" title={d.fingerprint ?? undefined}>
								{d.fingerprint ? `${d.fingerprint.slice(0, 19)}…` : "—"}
							</span>
						</Field>
						<Field label="判官">
							{d.judges.length} 个，其中进通过率{" "}
							{d.judges.filter((j) => j.verdict === "一票否决").length} 个
						</Field>
					</div>
				) : null}
			</CardContent>
		</Card>
	);
	if (q.isLoading || q.isError || !d)
		return (
			<>
				{head}
				<BlockState testId="plan-block-judges-list" q={q} failTestId="plan-judges-body-fail" />
			</>
		);

	const byKey = new Map(d.judges.map((j) => [j.key, j]));
	const cols: PlanColumn<PlanJudge>[] = [
		{
			key: "name",
			title: "判官",
			className: "w-32",
			render: (j) => <span className="font-medium">{j.name}</span>,
		},
		{
			key: "what",
			title: "判什么",
			className: "max-w-[420px]",
			render: (j) => (
				<span className="block truncate text-text-secondary" title={j.judges_what}>
					{j.judges_what}
				</span>
			),
		},
		{
			key: "verdict",
			title: "结论怎么用",
			className: "w-28",
			render: (j) => <Badge tone={VERDICT_TONE[j.verdict] ?? "muted"}>{j.verdict}</Badge>,
		},
		{
			key: "when",
			title: "什么时候跑",
			className: "max-w-[240px]",
			render: (j) => (
				<span className="block truncate" title={j.when}>
					{j.when}
				</span>
			),
		},
		{
			key: "prompt",
			title: "提示词",
			className: "w-40",
			render: (j) => (
				<span className={j.reuses || j.link ? "text-text-muted" : "font-mono text-xs"}>
					{promptText(j, byKey)}
				</span>
			),
		},
	];

	return (
		<>
			{head}
			<Card data-testid="plan-block-judges-list">
				<CardContent className="space-y-3 py-4">
					<div className="space-y-0.5">
						<div className="text-sm font-semibold text-text-primary">判官一览</div>
						<div className="text-xs text-text-muted">
							只有 NL 合规判官的结论进通过率（三维乘积里的合规维）；其余是观测列或诊断，不改过 ／ 败
						</div>
					</div>
					<PlanTable
						columns={cols}
						rows={d.judges}
						rowKey={(j) => j.key}
						density="compact"
						rowTestId="plan-judge-row"
					/>
				</CardContent>
			</Card>
			{d.judges.map((j) => (
				<JudgeCard key={j.key} j={j} byKey={byKey} />
			))}
		</>
	);
}
